import React, { useState, useEffect, useRef } from 'react';
import { Card, Avatar, Select, Row, Col, Typography, Progress, Button, message, Spin, Empty, Alert, Space } from 'antd';
import { StarFilled, ArrowLeftOutlined, ReloadOutlined, EditOutlined } from '@ant-design/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { 
  useUserDetails, 
  usePerformanceData, 
  useUserTasks, 
  useCommunicationData, 
  useSkillsData, 
  useTaskFilterOptions 
} from '../../../src/hooks/useUserRating';
import TasksSummaryDashboard from './TasksSummaryDashboard';
import SkillsManagementModal from './SkillsManagementModal';

const { Title, Text } = Typography;
const { Option } = Select;

const UserRatingDetailView = ({ user, onBack }) => {
  // Main filter state - this controls ALL sections
  const [mainViewMode, setMainViewMode] = useState('all');
  const [mainYear, setMainYear] = useState(new Date().getFullYear());
  const [mainPeriod, setMainPeriod] = useState(null);
  
  // Skills Modal state
  const [skillsModalVisible, setSkillsModalVisible] = useState(false);
  
  // Ref to track if component is mounted
  const isMountedRef = useRef(true);
  
  // Ref to prevent multiple simultaneous filter updates
  const filterUpdateTimeoutRef = useRef(null);

  // Generate year options (current year and 2 years back)
  const yearOptions = [];
  const currentYearValue = new Date().getFullYear();
  for (let i = 0; i < 3; i++) {
    yearOptions.push(currentYearValue - i);
  }

  // Generate quarter options
  const quarterOptions = ['Q1', 'Q2', 'Q3', 'Q4'];

  // Generate month options
  const monthOptions = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  // Calculate derived period for API calls based on main filter
  const getEffectivePeriod = () => {
    if (mainViewMode === 'all') return null;
    if (mainViewMode === 'yearly') return null;
    return mainPeriod;
  };

  const getEffectiveYear = () => {
    if (mainViewMode === 'all') return null;
    return mainYear;
  };

  // Determine chart view mode based on main filter
  const getChartViewMode = () => {
    if (mainViewMode === 'all') return 'monthly'; // Default when showing all data
    return mainViewMode; // Use the same view mode as main filter
  };

  // API hooks - all synchronized with main filter
  const { userDetails, loading: userLoading, error: userError, refetch: refetchUser, updateFilters: updateUserFilters } = useUserDetails(user?.id);
  
  const { performanceData, loading: performanceLoading, error: performanceError, refetch: refetchPerformance } = usePerformanceData(
    user?.id, 
    getChartViewMode(), 
    getChartViewMode() === 'yearly' ? getEffectiveYear() : null
  );
  
  const { tasksData, loading: tasksLoading, error: tasksError, updateFilters, updatePagination, refetch: refetchTasks } = useUserTasks(user?.id);
  
  // Communication - use main filter's period and year
  const { communicationData, loading: commLoading, error: commError, refetch: refetchCommunication } = useCommunicationData(
    user?.id, 
    getEffectivePeriod(),
    mainViewMode === 'all' ? 'monthly' : mainViewMode
  );
  
  // Skills - use main filter's period and year
  const { skillsData, loading: skillsLoading, error: skillsError, refetch: refetchSkills } = useSkillsData(
    user?.id, 
    getEffectivePeriod(),
    mainViewMode === 'all' ? 'monthly' : mainViewMode
  );
  
  const { filterOptions, loading: filterLoading, error: filterError, refetch: refetchFilterOptions } = useTaskFilterOptions(user?.id);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (filterUpdateTimeoutRef.current) {
        clearTimeout(filterUpdateTimeoutRef.current);
      }
    };
  }, []);

  // Debounced filter update to prevent rapid API calls
  const debouncedUpdateFilters = (filters) => {
    // Clear any pending timeout
    if (filterUpdateTimeoutRef.current) {
      clearTimeout(filterUpdateTimeoutRef.current);
    }
    
    // Set new timeout
    filterUpdateTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        console.log('Updating filters:', filters);
        updateUserFilters(filters);
      }
    }, 300); // 300ms debounce
  };

  // Update user details filter when main filter changes
  useEffect(() => {
    const filters = { 
      viewMode: mainViewMode, 
      period: getEffectivePeriod(), 
      year: getEffectiveYear() 
    };
    
    console.log('Main filter changed:', filters);
    debouncedUpdateFilters(filters);
    
    // Cleanup function
    return () => {
      if (filterUpdateTimeoutRef.current) {
        clearTimeout(filterUpdateTimeoutRef.current);
      }
    };
  }, [mainViewMode, mainPeriod, mainYear]);

  // Handle main view mode change
  const handleMainViewModeChange = (value) => {
    setMainViewMode(value);
    
    if (value === 'all') {
      setMainPeriod(null);
    } else if (value === 'yearly') {
      setMainPeriod(null);
    } else if (value === 'quarterly') {
      const defaultQuarter = 'Q' + (Math.floor((new Date().getMonth()) / 3) + 1);
      setMainPeriod(defaultQuarter);
    } else if (value === 'monthly') {
      const defaultMonth = monthOptions[new Date().getMonth()];
      setMainPeriod(defaultMonth);
    }
  };

  // Handle main year change
  const handleMainYearChange = (value) => {
    setMainYear(value);
  };

  // Handle main period change (quarter or month)
  const handleMainPeriodChange = (value) => {
    setMainPeriod(value);
  };

  // Get periods for upgrade section based on main view mode
  const getUpgradePeriods = () => {
    if (mainViewMode === 'all') {
      return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    } else if (mainViewMode === 'yearly') {
      return [`${mainYear - 2}`, `${mainYear - 1}`, `${mainYear}`];
    } else if (mainViewMode === 'quarterly') {
      return ['Q1', 'Q2', 'Q3', 'Q4'];
    } else {
      return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    }
  };

  // Enhanced data refresh
  const refreshAllData = () => {
    console.log('Refreshing all data...');
    message.loading('Refreshing data...', 1);
    
    Promise.all([
      refetchUser(),
      refetchPerformance(),
      refetchTasks(),
      refetchCommunication(),
      refetchSkills(),
      refetchFilterOptions()
    ]).then(() => {
      if (isMountedRef.current) {
        message.success('Data refreshed successfully');
      }
    }).catch((error) => {
      if (isMountedRef.current && !error?.cancelled) {
        message.error('Failed to refresh some data');
      }
    });
  };

  // Handle skills modal close with refresh
  const handleSkillsModalClose = () => {
    setSkillsModalVisible(false);
    refetchSkills();
    message.success('Skills updated');
  };

  // Enhanced error handling - filter out cancelled errors
  const hasRealErrors = () => {
    const errors = [userError, performanceError, tasksError, commError, skillsError, filterError];
    return errors.some(error => error && !error.cancelled);
  };
  
  if (hasRealErrors()) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={onBack}
          />
          <Title level={3} style={{ margin: 0 }}>Error Loading User Details</Title>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={refreshAllData}
            type="primary"
          >
            Retry
          </Button>
        </div>
        
        <Card>
          <Alert
            message="Failed to load some user data"
            description={
              <div>
                {userError && !userError.cancelled && <div><Text type="danger">User Details: {userError.message || userError}</Text></div>}
                {performanceError && !performanceError.cancelled && <div><Text type="danger">Performance Data: {performanceError.message || performanceError}</Text></div>}
                {tasksError && !tasksError.cancelled && <div><Text type="danger">Tasks Data: {tasksError.message || tasksError}</Text></div>}
                {commError && !commError.cancelled && <div><Text type="danger">Communication Data: {commError.message || commError}</Text></div>}
                {skillsError && !skillsError.cancelled && <div><Text type="danger">Skills Data: {skillsError.message || skillsError}</Text></div>}
                {filterError && !filterError.cancelled && <div><Text type="danger">Filter Options: {filterError.message || filterError}</Text></div>}
              </div>
            }
            type="error"
            showIcon
          />
        </Card>
      </div>
    );
  }

  // Get current data with fallbacks
  const userData = userDetails || user;
  const currentCommunicationData = communicationData || {
    verbal: { rating: 'Good', percentage: 75 },
    written: { rating: 'Average', percentage: 65 },
    coordination: { rating: 'Excellent', percentage: 85 }
  };
  const currentSkillsData = skillsData || {
    technical: ['Loading...'],
    artificial: ['Loading...'],
    ai: ['Loading...']
  };

  // Enhanced DonutChart component
  const DetailDonutChart = ({ data, total }) => {
    if (!data) {
      return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 260 }}>
        <Empty description="No performance data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>;
    }
    
    const { excellent = 0, good = 0, average = 0, belowAverage = 0 } = data;
    
    // Check if all values are zero
    if (excellent === 0 && good === 0 && average === 0 && belowAverage === 0) {
      return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 260, gap: 8 }}>
        <Empty description="No task data available" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        <Text type="secondary" style={{ fontSize: 12 }}>
          Try selecting a different time period
        </Text>
      </div>;
    }
    
    const chartData = [
      { name: 'Excellent', value: excellent, color: '#ff7a00' },
      { name: 'Good', value: good, color: '#52c41a' },
      { name: 'Average', value: average, color: '#722ed1' },
      { name: 'Below Average', value: belowAverage, color: '#1890ff' }
    ].filter(item => item.value > 0);

    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
      const RADIAN = Math.PI / 180;
      const radius = innerRadius + (outerRadius - innerRadius) * 1.4;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);

      return (
        <text 
          x={x} 
          y={y} 
          fill="#666666" 
          textAnchor="middle" 
          dominantBaseline="central"
          fontSize="14"
          fontWeight="600"
        >
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      );
    };

    return (
      <div style={{ width: 260, height: 260, position: 'relative' }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomizedLabel}
              outerRadius={100}
              innerRadius={60}
              fill="#8884d8"
              dataKey="value"
              startAngle={90}
              endAngle={450}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        
        <div 
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            pointerEvents: 'none'
          }}
        >
          <div style={{ fontSize: 32, fontWeight: 'bold', color: '#262626', lineHeight: 1 }}>{total}</div>
          <div style={{ fontSize: 14, color: '#8c8c8c', lineHeight: 1.2, marginTop: 4 }}>Total Task</div>
        </div>
      </div>
    );
  };

  // Enhanced LineChart component
  const CustomLineChart = ({ data, viewMode = 'monthly' }) => {
    if (!data || !Array.isArray(data)) {
      return <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty description="No performance data available" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>;
    }

    if (data.length === 0) {
      return <div style={{ height: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        <Empty description={`No ${viewMode} performance data found`} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        <Text type="secondary" style={{ fontSize: 12 }}>
          Try selecting a different time period
        </Text>
      </div>;
    }

    return (
      <div style={{ width: '100%', height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="period" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
            />
            <YAxis 
              domain={[1, 5]}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
            />
            <Tooltip />
            <Line 
              type="monotone" 
              dataKey="performance" 
              stroke="#ff7a00" 
              strokeWidth={2}
              dot={{ fill: '#ff7a00', strokeWidth: 2, r: 4 }}
            />
            <Line 
              type="monotone" 
              dataKey="proofreading" 
              stroke="#52c41a" 
              strokeWidth={2}
              dot={{ fill: '#52c41a', strokeWidth: 2, r: 4 }}
            />
            <Line 
              type="monotone" 
              dataKey="quality" 
              stroke="#722ed1" 
              strokeWidth={2}
              dot={{ fill: '#722ed1', strokeWidth: 2, r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Main loading state
  if (userLoading && !userData) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#f5f5f5', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <Spin size="large" tip="Loading user details..." />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <div style={{ padding: 24 }}>
        {/* User Info Header with Filters */}
        <Card style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
            {/* Only show back button if onBack function is provided */}
            {onBack && (
              <Button 
                type="text" 
                icon={<ArrowLeftOutlined />} 
                onClick={onBack}
                style={{ marginRight: 8 }}
              />
            )}
            <Avatar 
              src={userData?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(userData?.name || 'User')}&background=random`}
              size={56} 
            />
            <div>
              <Title level={3} style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>
                {userData?.name || 'Loading...'}
              </Title>
              <Text style={{ color: '#666', fontSize: 14 }}>{userData?.role || 'Loading...'}</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8 }}>
              <StarFilled style={{ color: '#faad14', fontSize: 16 }} />
              <Text style={{ fontSize: 18, fontWeight: 600 }}>{userData?.rating || '--'}</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 40, marginLeft: 'auto' }}>
              <div style={{ 
                backgroundColor: '#e6f7ff', 
                padding: '12px 16px', 
                borderRadius: 8, 
                textAlign: 'center',
                minWidth: 100
              }}>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                  {userData?.totalTasks || '---'}
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>Total Task</div>
              </div>
              <div style={{ 
                backgroundColor: '#e6f7ff', 
                padding: '12px 16px', 
                borderRadius: 8, 
                textAlign: 'center',
                minWidth: 100
              }}>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                  {userData?.completedTasks || '---'}
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>Task Completed</div>
              </div>
              <div style={{ 
                backgroundColor: '#e6f7ff', 
                padding: '12px 16px', 
                borderRadius: 8, 
                textAlign: 'center',
                minWidth: 100
              }}>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                  {userData?.onTimeDelivery || '--'}%
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>On-Time Delivery %</div>
              </div>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={refreshAllData}
                type="text"
              >
                Refresh
              </Button>
            </div>
          </div>

          {/* Main Filter Controls */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            alignItems: 'center', 
            gap: 12,
            paddingTop: 16,
            borderTop: '1px solid #f0f0f0'
          }}>
            <Space size="middle">
              {/* View Mode Selection */}
              <Select
                value={mainViewMode}
                onChange={handleMainViewModeChange}
                style={{ width: 120 }}
                placeholder="Select View"
                size="small"
              >
                <Option value="all">All Time</Option>
                <Option value="yearly">Yearly</Option>
                <Option value="quarterly">Quarterly</Option>
                <Option value="monthly">Monthly</Option>
              </Select>

              {/* Year Selection - Show for yearly, quarterly, and monthly */}
              {(mainViewMode === 'yearly' || mainViewMode === 'quarterly' || mainViewMode === 'monthly') && (
                <Select
                  value={mainYear}
                  onChange={handleMainYearChange}
                  style={{ width: 100 }}
                  placeholder="Year"
                  size="small"
                >
                  {yearOptions.map(year => (
                    <Option key={year} value={year}>{year}</Option>
                  ))}
                </Select>
              )}

              {/* Quarter Selection - Show only for quarterly */}
              {mainViewMode === 'quarterly' && (
                <Select
                  value={mainPeriod}
                  onChange={handleMainPeriodChange}
                  style={{ width: 100 }}
                  placeholder="Quarter"
                  size="small"
                >
                  {quarterOptions.map(quarter => (
                    <Option key={quarter} value={quarter}>{quarter}</Option>
                  ))}
                </Select>
              )}

              {/* Month Selection - Show only for monthly */}
              {mainViewMode === 'monthly' && (
                <Select
                  value={mainPeriod}
                  onChange={handleMainPeriodChange}
                  style={{ width: 100 }}
                  placeholder="Month"
                  size="small"
                >
                  {monthOptions.map(month => (
                    <Option key={month} value={month}>{month}</Option>
                  ))}
                </Select>
              )}
            </Space>
          </div>

          {/* Active Filter Info */}
          {mainViewMode !== 'all' && (
            <div style={{ paddingTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Showing data for: <Text strong>
                  {mainViewMode === 'yearly' && `Year ${mainYear}`}
                  {mainViewMode === 'quarterly' && `${mainPeriod} ${mainYear}`}
                  {mainViewMode === 'monthly' && `${mainPeriod} ${mainYear}`}
                </Text>
              </Text>
            </div>
          )}
        </Card>

        {/* Charts Section */}
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={[40, 0]}>
            <Col span={8}>
              <Card style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {userLoading ? (
                  <Spin />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 12, height: 12, backgroundColor: '#ff7a00', borderRadius: '50%' }}></div>
                        <Text style={{ fontSize: 14, color: '#666' }}>Performance</Text>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 12, height: 12, backgroundColor: '#52c41a', borderRadius: '50%' }}></div>
                        <Text style={{ fontSize: 14, color: '#666' }}>Proofreading</Text>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 12, height: 12, backgroundColor: '#722ed1', borderRadius: '50%' }}></div>
                        <Text style={{ fontSize: 14, color: '#666' }}>Quality</Text>
                      </div>
                    </div>
                    <DetailDonutChart 
                      data={userData?.performance} 
                      total={userData?.totalTasks || 0} 
                    />
                  </div>
                )}
              </Card>
            </Col>
            <Col span={16}>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 3, height: 3, backgroundColor: '#ff7a00', borderRadius: '50%' }}></div>
                    <Text style={{ fontSize: 12, color: '#666' }}>Performance</Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 3, height: 3, backgroundColor: '#52c41a', borderRadius: '50%' }}></div>
                    <Text style={{ fontSize: 12, color: '#666' }}>Proofreading</Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 3, height: 3, backgroundColor: '#722ed1', borderRadius: '50%' }}></div>
                    <Text style={{ fontSize: 12, color: '#666' }}>Quality</Text>
                  </div>
                </div>
              </div>
              {performanceLoading ? (
                <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Spin tip="Loading performance data..." />
                </div>
              ) : (
                <CustomLineChart data={performanceData} viewMode={getChartViewMode()} />
              )}
            </Col>
          </Row>
        </Card>

        {/* Skills and Communication */}
        <div style={{ display: 'flex', gap: 24, marginBottom: 24 }}>
          <div style={{ flex: '3' }}>
            <Card>
              {/* SKILLS SECTION WITH MANAGE BUTTON */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={4} style={{ margin: 0 }}>
                  <Text style={{ color: '#14B253', fontSize: 24, fontWeight: 600 }}>Upgrade</Text> 
                  <Text style={{ color: '#646464', fontSize: 18, fontWeight: 500, marginLeft: 8 }}>New skill set</Text>
                </Title>
                {/* MANAGE SKILLS BUTTON */}
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => setSkillsModalVisible(true)}
                  size="small"
                  style={{ 
                    backgroundColor: '#14B253',
                    borderColor: '#14B253',
                    fontWeight: 500
                  }}
                >
                  Manage Skills
                </Button>
              </div>
              
              <div style={{ display: 'flex', gap: 32, marginBottom: 24, flexWrap: 'wrap' }}>
                {getUpgradePeriods().map(period => {
                  const isSelected = mainViewMode === 'all' 
                    ? false 
                    : period === mainPeriod || (mainViewMode === 'yearly' && period === String(mainYear));
                  
                  return (
                    <div 
                      key={period}
                      style={{ 
                        padding: '8px 0',
                        cursor: 'default',
                        color: isSelected ? '#14B253' : '#999',
                        fontSize: 18,
                        fontWeight: 500,
                        borderBottom: isSelected ? '3px solid #14B253' : '2px solid transparent',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {period}
                    </div>
                  );
                })}
              </div>
              
              {skillsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                  <Spin tip="Loading skills data..." />
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 32 }}>
                  <div style={{ flex: 1 }}>
                    <Title level={5} style={{ marginBottom: 12, fontSize: 18 }}>Technical -</Title>
                    {(currentSkillsData.technical || ['No data available']).map((skill, i) => (
                      <div key={i} style={{ fontSize: 14, color: '#524F4F', marginBottom: 6 }}>• {skill}</div>
                    ))}
                  </div>
                  <div style={{ flex: 1 }}>
                    <Title level={5} style={{ marginBottom: 12, fontSize: 18 }}>Artificial -</Title>
                    {(currentSkillsData.artificial || ['No data available']).map((skill, i) => (
                      <div key={i} style={{ fontSize: 14, color: '#524F4F', marginBottom: 6 }}>• {skill}</div>
                    ))}
                  </div>
                  <div style={{ flex: 1 }}>
                    <Title level={5} style={{ marginBottom: 12, fontSize: 18 }}>AI -</Title>
                    {(currentSkillsData.ai || ['No data available']).map((skill, i) => (
                      <div key={i} style={{ fontSize: 14, color: '#524F4F', marginBottom: 6 }}>• {skill}</div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </div>

          <div style={{ flex: '1' }}>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={4} style={{ margin: 0, color: '#14B253', fontSize: '24px' }}>Communication</Title>
              </div>
              
              {commLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                  <Spin tip="Loading communication data..." />
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <Text style={{ fontSize: 14, color: '#666' }}>Coordination</Text>
                      <Text style={{ fontSize: 14, fontWeight: 500 }}>
                        {currentCommunicationData.coordination?.rating} {currentCommunicationData.coordination?.percentage}%
                      </Text>
                    </div>
                    <Progress 
                      percent={currentCommunicationData.coordination?.percentage || 0} 
                      strokeColor="#9282AD" 
                      showInfo={false} 
                      size="medium" 
                    />
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Tasks Summary Dashboard */}
        <TasksSummaryDashboard 
          userId={user?.id} 
          mainViewMode={mainViewMode}
          mainYear={mainYear}
          mainPeriod={mainPeriod}
        />
      </div>

      {/* SKILLS MANAGEMENT MODAL */}
      <SkillsManagementModal
        visible={skillsModalVisible}
        onClose={handleSkillsModalClose}
        userId={user?.id}
        managerId={1} // Replace with actual logged-in manager ID
      />
    </div>
  );
};

export default UserRatingDetailView;